import { FC, useMemo } from 'react';
import { CartIcon } from '../icons';
import { NormalizedProductHit } from '../../../types';
import { useProductLeadtime } from '../../../use/product-inventory';
import { type InventoryPolicy } from '../../../../helpers/availability-lead-time';

type GridViewHitProps = {
  hit: NormalizedProductHit;
  mobile: boolean;
};

// TODO: display loading state when the user adds item to cart.
export const GridViewHit: FC<GridViewHitProps> = ({ hit, mobile }) => {
  const { availabilityStatus, leadTime, backupTitle } = useProductLeadtime(
    hit.handle,
    hit.leadTimeArray,
    hit.inventoryPolicy as InventoryPolicy,
    hit.variantsInventoryCount,
    hit.useUp
  );

  const title =
    typeof hit.title === 'string'
      ? hit.title
      : backupTitle ?? <span style={{ fontStyle: 'italic' }}>{hit.handle}</span>;

  const titleString = typeof hit.title === 'string' ? hit.title : hit.handle;

  const [availabilityClass, availabilityStatusText, productIsAvailable] = useMemo(() => {
    let className = 'hit-availability';
    let statusText;
    let productAvailable = true;

    if (availabilityStatus === 'stock') {
      statusText = theme.locales.inStock;
    } else if (availabilityStatus === 'backorder') {
      statusText = theme.locales.backOrder.replace('[days]', String(leadTime));
    } else {
      if (availabilityStatus === 'unavailable') {
        statusText = theme.locales.unavailable;
      } else {
        statusText = theme.locales.onRequest;
      }

      className = `${className} hit-unavailable`;
      productAvailable = false;
    }

    return [className, statusText, productAvailable];
  }, [availabilityStatus, leadTime]);

  const price = useMemo(() => {
    return (
      productIsAvailable && (
        <>
          {hit.price}
          {hit.priceExclVAT && (
            <span className="excl-vat">
              ({hit.priceExclVAT} {theme.locales.exclVAT})
            </span>
          )}
        </>
      )
    );
  }, [hit.price, hit.priceExclVAT, productIsAvailable]);

  const addToCart = () => {
    hit.onClickCartIcon({
      quantity: '1',
      availability: availabilityStatus,
      lead_time: leadTime,
    });
  };

  if (mobile) {
    return (
      <>
        <div className="mb-image-and-text">
          <div className="image-container">
            <a href={hit.url}>
              <img className="image" src={hit.image} alt={hit.imageAltName} height={90} width={90} />
            </a>
          </div>

          <div className="info-container">
            <h3 className="title">
              <a href={hit.url}>{title}</a>
            </h3>
            <div className="sku">{hit.sku + (hit.sku !== hit.MPN && hit.MPN !== '' ? ` / ${hit.MPN}` : ``)}</div>
          </div>
        </div>
        <div className="mb-price-and-cart">
          <div className="price">{price}</div>

          <div className="availability-and-cart">
            <div className={availabilityClass}>{availabilityStatusText}</div>

            <button
              name="add"
              className="icon-button hit-buy-button"
              onClick={addToCart}
              disabled={!productIsAvailable}
              aria-label="Add to cart"
            >
              <CartIcon />
            </button>

            <input type="hidden" name="id" value={hit.objectID} />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <a className="image-container" href={hit.url}>
        <img className="image" src={hit.image} alt={hit.imageAltName} height={108} width={108} />
      </a>

      <div className="info-container">
        <h3 className="title">
          <a
            href={hit.url}
            title={titleString}
            style={{
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              lineClamp: 3,
              WebkitLineClamp: 3,
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
            }}
          >
            {title}
          </a>
        </h3>
        <div className="sku">{hit.sku + (hit.sku !== hit.MPN && hit.MPN !== '' ? ` / ${hit.MPN}` : ``)}</div>
        <div className="price">{price}</div>
      </div>

      <div className="cart-container">
        <button
          name="add"
          className="icon-button hit-buy-button"
          onClick={addToCart}
          disabled={!productIsAvailable}
          aria-label="Add to cart"
        >
          <CartIcon />
        </button>

        <input type="hidden" name="id" value={hit.objectID} />

        <div className={availabilityClass}>{availabilityStatusText}</div>
      </div>
    </>
  );
};
